import { formatMonthYearPeriod, getMonthShortAbbreviation } from '@common/neo.tax';
import { Loading, Stack, Typography, colors } from '@ui-components/neo.tax';

import { useGetDocumentEventSignedUrl } from './hooks';
import { AscSnapshotDocumentEvent, useAscSnapshotDocumentEvents } from './hooks/useAscSnapshotDocumentEvents';

const AscSnapshotItem = ({ event }: { event: AscSnapshotDocumentEvent }): React.ReactElement => {
  const { getDocumentEventSignedUrl, loading: getDocumentEventSignedUrlLoading } = useGetDocumentEventSignedUrl();
  const handleDownload = async (): Promise<void> => {
    const { data } = await getDocumentEventSignedUrl({ variables: { documentEventId: event.id } });
    if (data) {
      window.open(data.getDocumentEventSignedUrl, '_blank');
    }
  };

  const getPeriodLabel = (event: AscSnapshotDocumentEvent): string => {
    if (event.periodName.charAt(0) === 'M') {
      return formatMonthYearPeriod(event.period).start;
    }

    const periodLabel =
      event.period.startMonthYear.year === event.period.endMonthYear.year
        ? `${getMonthShortAbbreviation(event.period.startMonthYear.month)} - ${formatMonthYearPeriod(event.period).end}`
        : `${formatMonthYearPeriod(event.period).start} - ${formatMonthYearPeriod(event.period).end}`;

    if (event.periodName.charAt(0) === 'A') {
      return `TY ${event.period.startMonthYear.year} Annual (${periodLabel})`;
    }

    return `TY ${event.period.startMonthYear.year} ${event.periodName} (${periodLabel})`;
  };

  return (
    <Stack as="li" alignItems="center" justifyContent="space-between" key={event.id} paddingY={1} paddingX={2}>
      <Typography fontSize="xs" color={colors.gray[700]}>
        {getPeriodLabel(event)}
      </Typography>
      <Typography
        as="button"
        fontSize="xs"
        disabled={getDocumentEventSignedUrlLoading}
        color={getDocumentEventSignedUrlLoading ? colors.gray[300] : colors.primary[600]}
        onClick={handleDownload}
      >
        {getDocumentEventSignedUrlLoading ? 'Downloading...' : 'Download'}
      </Typography>
    </Stack>
  );
};

export const AscSnapshotList = (): React.ReactElement => {
  const { documentEvents, loading: documentEventsLoading } = useAscSnapshotDocumentEvents();

  if (documentEventsLoading) {
    return <Loading height={80} />;
  }

  if (!documentEvents || documentEvents.length === 0) {
    return (
      <Typography fontSize="xs" color={colors.gray[600]}>
        No previous ASC 350-40 documents found.
      </Typography>
    );
  }

  // Group document events by period type
  const groupedEvents = documentEvents.reduce((acc: Record<string, AscSnapshotDocumentEvent[]>, event) => {
    const firstChar = event.periodName.charAt(0).toUpperCase();
    let groupKey;

    switch (firstChar) {
      case 'M':
        groupKey = 'Monthly';
        break;
      case 'Q':
        groupKey = 'Quarterly';
        break;
      case 'H':
        groupKey = 'Half-Year';
        break;
      case 'A':
        groupKey = 'Annual';
        break;
      default:
        groupKey = 'Other';
    }

    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(event);

    return acc;
  }, {});

  return (
    <Stack direction="column" gap={3} fullWidth>
      {Object.entries(groupedEvents).map(([groupName, events]) => (
        <Stack key={groupName} direction="column" gap={2} fullWidth>
          <Stack direction="column" gap={2} fullWidth backgroundColor={colors.gray[100]} paddingY={1} paddingX={2}>
            <Typography fontSize="sm" fontWeight="medium" color={colors.gray[900]}>
              {groupName}
            </Typography>
          </Stack>
          <Stack direction="column" gap={2} fullWidth paddingX={2}>
            {events.map((event) => (
              <AscSnapshotItem key={event.id} event={event} />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};
